/**
 * PDP alternative-product suggestion.
 *
 * When the selected size of the current product is sold out, looks up an
 * in-stock alternative (same type group, same colour, same size) via the
 * Storefront API and renders it below the Add to Cart button. Hooks into the
 * existing PUB_SUB_EVENTS.variantChange event (see product-variants.js) so it
 * reuses the theme's existing size-selection mechanism rather than adding a
 * new one.
 */
(() => {
  const readJSON = selector => {
    const el = document.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  const getTypeGroups = () => readJSON("[data-pdp-alt-type-groups]") || [];

  // Types compatible with productType: every type sharing at least one
  // configured group with it (including itself, since it's a member of that
  // group). If productType isn't in any configured group at all, there's no
  // match by design — returns empty, not a same-type-only fallback.
  const compatibleTypes = (productType, groups) => {
    const set = new Set();
    for (const group of groups) {
      const types = group.types || [];
      const hasMatch = types.some(t => t.toLowerCase() === productType.toLowerCase());
      if (hasMatch) {
        for (const t of types) set.add(t);
      }
    }
    return Array.from(set);
  };

  const escapeQueryValue = value => String(value).replace(/"/g, '\\"');

  const buildQuery = types => `(${types.map(t => `product_type:"${escapeQueryValue(t)}"`).join(" OR ")})`;

  const CANDIDATES_QUERY = `
    query PdpAlternatives($query: String!) {
      products(first: 250, sortKey: CREATED_AT, reverse: true, query: $query) {
        edges {
          node {
            id
            handle
            title
            featuredImage { url altText }
            options { name values }
            variants(first: 100) {
              edges { node { id availableForSale selectedOptions { name value } } }
            }
          }
        }
      }
    }
  `;

  const fetchCandidates = async types => {
    if (!types.length) return [];

    const endpoint = `https://${window.shopPermanentDomain}/api/${window.pdpAltApiVersion}/graphql.json`;

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": window.pdpAltStorefrontToken
        },
        body: JSON.stringify({
          query: CANDIDATES_QUERY,
          variables: { query: buildQuery(types) }
        })
      });

      if (!res.ok) return [];
      const json = await res.json();
      if (json.errors) return [];
      return (json.data?.products?.edges || []).map(edge => edge.node);
    } catch (err) {
      return [];
    }
  };

  const findColorValue = product => {
    const opt = (product.options || []).find(o => {
      const n = o.name.toLowerCase();
      return n === "color" || n === "colour";
    });
    return opt?.values?.[0] || null;
  };

  const findVariantForSize = (product, sizeOptionName, sizeValue) => {
    const edges = product.variants?.edges || [];
    for (const edge of edges) {
      const variant = edge.node;
      const selected = (variant.selectedOptions || []).find(
        o => o.name.toLowerCase() === sizeOptionName.toLowerCase()
      );
      if (selected && selected.value === sizeValue && variant.availableForSale) {
        return variant;
      }
    }
    return null;
  };

  const pickMatch = (products, context, sizeValue) => {
    const currentGid = `gid://shopify/Product/${context.productId}`;
    for (const product of products) {
      if (product.id === currentGid) continue;

      const color = findColorValue(product);
      if (!color || color.toLowerCase() !== context.color.toLowerCase()) continue;

      const variant = findVariantForSize(product, context.sizeOptionName, sizeValue);
      if (variant) return { product, variant };
    }
    return null;
  };

  const variantNumericId = gid => /\/(\d+)$/.exec(gid || "")?.[1] || null;

  const renderMatch = (container, match, sizeValue) => {
    const thumb = container.querySelector("[data-pdp-alt-thumb]");
    const eyebrow = container.querySelector("[data-pdp-alt-eyebrow]");
    const link = container.querySelector("[data-pdp-alt-link]");

    if (thumb) {
      const imageUrl = match.product.featuredImage?.url;
      thumb.src = imageUrl || "";
      thumb.alt = match.product.featuredImage?.altText || match.product.title || "";
    }

    if (eyebrow) {
      const template = window.pdpAltStrings?.alsoAvailableInSize || "Also available in size {size}";
      eyebrow.textContent = template.replace("{size}", sizeValue);
    }

    if (link) {
      const variantId = variantNumericId(match.variant.id);
      link.textContent = match.product.title;
      link.href = `/products/${match.product.handle}${variantId ? `?variant=${variantId}` : ""}`;
    }

    container.hidden = false;
  };

  const hideAlt = container => {
    if (container) container.hidden = true;
  };

  const init = () => {
    if (
      !window.pdpAltEnabled ||
      !window.pdpAltStorefrontToken ||
      !window.shopPermanentDomain ||
      !window.pdpAltApiVersion
    ) {
      return;
    }

    const context = readJSON("[data-pdp-alt-context]");
    if (!context || !context.productType || !context.color) return;

    let requestToken = 0;

    const checkAndSuggest = async (available, options) => {
      const container = document.getElementById(`PdpAltSuggestion-${context.sectionId}`);
      if (!container) return;

      if (available !== false) {
        hideAlt(container);
        return;
      }

      const sizeOptionName = context.sizeOptionName || "Size";
      const sizeIndex = (context.optionNames || []).findIndex(
        name => name.toLowerCase() === sizeOptionName.toLowerCase()
      );
      const sizeValue = sizeIndex >= 0 && options ? options[sizeIndex] : null;

      if (!sizeValue) {
        hideAlt(container);
        return;
      }

      const groups = getTypeGroups();
      const types = compatibleTypes(context.productType, groups);
      const myToken = ++requestToken;

      try {
        const products = await fetchCandidates(types);
        const match = pickMatch(products, context, sizeValue);

        if (myToken !== requestToken) return; // a newer selection superseded this request

        if (match) {
          renderMatch(container, match, sizeValue);
        } else {
          hideAlt(container);
        }
      } catch (err) {
        if (myToken === requestToken) hideAlt(container);
      }
    };

    // Cover the case where the size selected on initial page load is already sold out.
    checkAndSuggest(context.initialVariantAvailable, context.initialVariantOptions);

    subscribe(PUB_SUB_EVENTS.variantChange, event => {
      const data = event?.data;
      if (!data || data.sectionId !== context.sectionId) return;
      checkAndSuggest(data.variant ? data.variant.available : false, data.variant?.options || null);
    });
  };
  
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
